import { useEffect, useMemo, useState } from 'react';
import {
  collection, onSnapshot, query, orderBy, addDoc, updateDoc, doc, getDocs, where,
} from 'firebase/firestore';
import { FileText, Plus, Download, Mail, Check, Ban, X, Trash2 } from 'lucide-react';
import { db, sendGraceHavenEmail } from '@/lib/firebase';
import { useBusiness } from '@/lib/business';
import { formatPula, round2 } from '@/lib/money';
import { nextNumber } from '@/lib/numbering';
import { downloadFinancePdf, financePdfBase64, financePdfFilename } from '@/lib/financePdf';
import type { FinanceDoc, DocLineItem, DocStatus, Booking, StayRequest, CarHireRequest } from '@/lib/types';
import { useTreatments } from '@/lib/useTreatments';
import { ROOMS } from '@/data/accommodation';
import { Modal } from '@/components/ui/Modal';

type Draft = {
  kind: 'invoice' | 'receipt' | 'quotation';
  clientName: string;
  clientEmail: string;
  items: DocLineItem[];
  vatRate: number;
  notes: string;
};

const blankDraft = (): Draft => ({
  kind: 'invoice',
  clientName: '',
  clientEmail: '',
  items: [{ description: '', qty: 1, unitPrice: 0 }],
  vatRate: 0,
  notes: '',
});

const STATUS_STYLE: Record<DocStatus, string> = {
  draft: 'bg-espresso/10 text-espresso',
  sent: 'bg-gold/15 text-gold-deep',
  paid: 'bg-emerald-100 text-emerald-700',
  void: 'bg-blush-soft text-blush-deep line-through',
};

export function Invoices() {
  const biz = useBusiness();
  const { all: treatments } = useTreatments();
  const [docs, setDocs] = useState<FinanceDoc[]>([]);
  const [filter, setFilter] = useState<'all' | 'invoice' | 'receipt' | 'quotation'>('all');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(blankDraft());
  const [clients, setClients] = useState<{ name: string; email?: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [giftCode, setGiftCode] = useState('');
  const [financeNotice, setFinanceNotice] = useState<{ tone: 'success' | 'error'; title: string; body: string } | null>(null);
  // Prefill a document from an existing stay, spa booking, or car hire.
  const [source, setSource] = useState<'manual' | 'stay' | 'booking' | 'carhire'>('manual');
  const [pickedId, setPickedId] = useState('');
  const [stays, setStays] = useState<StayRequest[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [hires, setHires] = useState<CarHireRequest[]>([]);

  useEffect(() => {
    const q = query(collection(db, 'documents'), orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => setDocs(snap.docs.map((dc) => ({ id: dc.id, ...(dc.data() as Omit<FinanceDoc, 'id'>) }))),
      () => setDocs([]),
    );
  }, []);

  useEffect(() => {
    getDocs(collection(db, 'clients'))
      .then((s) => setClients(s.docs.map((dc) => ({ name: (dc.data().fullName as string) || '', email: dc.data().email as string | undefined }))))
      .catch(() => setClients([]));
  }, []);

  const shown = useMemo(() => (filter === 'all' ? docs : docs.filter((d) => d.kind === filter)), [docs, filter]);
  const subtotal = round2(draft.items.reduce((s, it) => s + it.qty * it.unitPrice, 0));
  const vat = round2(subtotal * draft.vatRate);
  const total = round2(subtotal + vat);

  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 3500); }
  function setItem(i: number, patch: Partial<DocLineItem>) {
    setDraft((d) => ({ ...d, items: d.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));
  }

  function openCreate() {
    setDraft(blankDraft());
    setGiftCode('');
    setSource('manual');
    setPickedId('');
    setOpen(true);
  }

  // Lazily load stays / bookings / hires the first time the operator picks that source.
  async function chooseSource(next: 'manual' | 'stay' | 'booking' | 'carhire') {
    setSource(next);
    setPickedId('');
    try {
      if (next === 'stay' && stays.length === 0) {
        const s = await getDocs(query(collection(db, 'stayRequests'), orderBy('createdAt', 'desc')));
        setStays(s.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<StayRequest, 'id'>) })));
      } else if (next === 'booking' && bookings.length === 0) {
        const s = await getDocs(query(collection(db, 'bookings'), orderBy('createdAt', 'desc')));
        setBookings(s.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Booking, 'id'>) })));
      } else if (next === 'carhire' && hires.length === 0) {
        const s = await getDocs(query(collection(db, 'carHireRequests'), orderBy('createdAt', 'desc')));
        setHires(s.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CarHireRequest, 'id'>) })));
      }
    } catch {
      flash('Could not load records to prefill from.');
    }
  }

  function prefillFromStay(s: StayRequest) {
    const nights = Math.max(1, Number(s.nights || 1));
    // One line per room (room rate × nights); fall back to the legacy single total.
    const items: DocLineItem[] = (s.rooms && s.rooms.length)
      ? s.rooms.map((r) => ({ description: `${r.roomName} — ${nights} night${nights === 1 ? '' : 's'}`, qty: 1, unitPrice: round2(Number(r.price || 0) * nights) }))
      : [{ description: `${s.roomName || 'Room'} — ${nights} night${nights === 1 ? '' : 's'}`, qty: 1, unitPrice: Number(s.totalPrice || 0) }];
    setDraft((d) => ({ ...d, clientName: s.fullName, clientEmail: s.email || '', items }));
  }

  function prefillFromBooking(b: Booking) {
    const items = (b.selectedServices || []).map((sv) => ({ description: sv.name, qty: 1, unitPrice: Number(sv.price || 0) }));
    setDraft((d) => ({
      ...d,
      clientName: b.fullName,
      clientEmail: b.email || '',
      items: items.length ? items : [{ description: '', qty: 1, unitPrice: 0 }],
    }));
  }

  function prefillFromCarHire(h: CarHireRequest) {
    const days = Math.max(1, Number(h.days || 1));
    const unit = h.pricePerDay ? round2(Number(h.pricePerDay) * days) : Number(h.totalPrice || 0);
    setDraft((d) => ({
      ...d,
      clientName: h.fullName,
      clientEmail: h.email || '',
      items: [{ description: `${h.vehicleName || 'Vehicle'} hire — ${days} day${days === 1 ? '' : 's'}`, qty: 1, unitPrice: unit }],
    }));
  }

  function pickRecord(id: string) {
    setPickedId(id);
    if (!id) return;
    if (source === 'stay') { const s = stays.find((x) => x.id === id); if (s) prefillFromStay(s); }
    else if (source === 'booking') { const b = bookings.find((x) => x.id === id); if (b) prefillFromBooking(b); }
    else if (source === 'carhire') { const h = hires.find((x) => x.id === id); if (h) prefillFromCarHire(h); }
  }

  async function create() {
    if (!draft.clientName.trim()) return flash('Add a client name.');
    if (draft.items.every((it) => !it.description.trim())) return flash('Add at least one line item.');
    setBusy(true);
    try {
      const now = new Date().toISOString();
      // Optional gift-card redemption (receipts only). Surface any problem as a
      // full-screen modal (keeping the form open) so it can't be missed.
      const code = giftCode.trim().toUpperCase();
      let gcId = '';
      let gcBalance = 0;
      let gcApplied = 0;
      if (draft.kind === 'receipt' && code) {
        try {
          const snap = await getDocs(query(collection(db, 'giftCards'), where('code', '==', code)));
          if (snap.empty) throw new Error('Gift card code not found.');
          const gc = snap.docs[0].data();
          if (gc.status !== 'active') throw new Error('That gift card is not active.');
          gcBalance = Number(gc.balance ?? gc.amount ?? 0);
          if (gcBalance <= 0) throw new Error('That gift card has no balance left.');
          gcId = snap.docs[0].id;
          gcApplied = Math.min(gcBalance, total);
        } catch (ge) {
          setFinanceNotice({
            tone: 'error',
            title: 'Gift card not applied',
            body: ge instanceof Error ? ge.message : 'That gift card could not be redeemed.',
          });
          return; // finally still runs setBusy(false); form stays open to fix the code
        }
      }

      const number = await nextNumber(draft.kind);
      const payload: Record<string, unknown> = {
        number, kind: draft.kind,
        clientName: draft.clientName.trim(),
        items: draft.items.filter((it) => it.description.trim()),
        subtotal, vatRate: draft.vatRate, vat, total,
        status: draft.kind === 'receipt' ? 'paid' : 'draft',
        createdAt: now, updatedAt: now,
      };
      const ce = draft.clientEmail.trim();
      if (ce) payload.clientEmail = ce;
      const nt = draft.notes.trim();
      if (nt) payload.notes = nt;
      if (gcApplied > 0) { payload.giftCardCode = code; payload.giftCardApplied = gcApplied; }

      await addDoc(collection(db, 'documents'), payload);
      if (gcId) {
        const newBal = round2(gcBalance - gcApplied);
        await updateDoc(doc(db, 'giftCards', gcId), {
          balance: newBal, status: newBal <= 0 ? 'redeemed' : 'active', updatedAt: now, redeemedAgainst: number,
        });
        setFinanceNotice({
          tone: 'success',
          title: 'Gift card redeemed',
          body: `${code} applied ${formatPula(gcApplied)} to ${number}. Remaining balance ${formatPula(newBal)}.`,
        });
      }
      setGiftCode('');
      setOpen(false);
      setDraft(blankDraft());
      if (!gcId) flash(`${number} created.`);
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not create.');
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(dc: FinanceDoc, status: DocStatus) {
    try {
      await updateDoc(doc(db, 'documents', dc.id), { status, updatedAt: new Date().toISOString() });
      flash(`Marked ${status}.`);
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Update failed.');
    }
  }

  async function emailDoc(dc: FinanceDoc) {
    if (!dc.clientEmail) return flash('No client email on this document.');
    setBusy(true);
    try {
      const base64 = financePdfBase64(dc, biz);
      const label = dc.kind === 'invoice' ? 'invoice' : dc.kind === 'quotation' ? 'quotation' : 'receipt';
      const body =
        `Dear ${dc.clientName.split(' ')[0]},\n\n` +
        `Please find your ${label} ${dc.number} attached${dc.kind === 'invoice' || dc.kind === 'quotation' ? `, for a total of ${formatPula(dc.total)}` : ''}.\n\n` +
        `Thank you for choosing ${biz.name}.`;
      await sendGraceHavenEmail({
        to: dc.clientEmail,
        subject: `Your ${label} ${dc.number} — ${biz.name}`,
        body,
        attachments: [{ filename: financePdfFilename(dc), contentBase64: base64, contentType: 'application/pdf' }],
        relatedEntityType: dc.kind,
        relatedEntityId: dc.id,
      });
      await updateDoc(doc(db, 'documents', dc.id), {
        status: dc.status === 'draft' ? 'sent' : dc.status,
        emailSentAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      flash(`Emailed ${dc.number} to ${dc.clientEmail}.`);
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Email failed (check email config).');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-espresso">Invoices &amp; Receipts</h1>
          <p className="mt-1 font-body text-sm text-muted">Create branded PDFs, email them, and track payment.</p>
        </div>
        <button onClick={openCreate} className="btn-primary !px-5 !py-2.5">
          <Plus size={16} /> New document
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {(['all', 'invoice', 'receipt', 'quotation'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`chip ${filter === f ? 'border-gold bg-gold/15 text-espresso' : 'border-line bg-white text-muted hover:border-gold'}`}>
            {f === 'all' ? 'All' : f + 's'}
          </button>
        ))}
      </div>

      <div className="mt-5 overflow-x-auto border border-line bg-paper">
        <table className="w-full min-w-[680px] text-left">
          <thead>
            <tr className="border-b border-line font-body text-[11px] uppercase tracking-wide text-muted">
              <th className="px-5 py-3">Number</th><th className="px-5 py-3">Client</th>
              <th className="px-5 py-3">Total</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((dc) => (
              <tr key={dc.id} className="border-b border-line last:border-0 hover:bg-cream/40">
                <td className="px-5 py-3.5">
                  <span className="flex items-center gap-2 font-body text-sm font-semibold text-espresso">
                    <FileText size={15} className="text-gold-deep" /> {dc.number}
                  </span>
                  <span className="font-body text-xs capitalize text-muted">{dc.kind}</span>
                </td>
                <td className="px-5 py-3.5">
                  <p className="font-body text-sm text-espresso">{dc.clientName}</p>
                  <p className="font-body text-xs text-muted">{dc.clientEmail || '—'}</p>
                </td>
                <td className="px-5 py-3.5 font-display text-base text-espresso">{formatPula(dc.total)}</td>
                <td className="px-5 py-3.5">
                  <span className={`inline-block rounded-none px-2.5 py-1 font-body text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLE[dc.status]}`}>{dc.status}</span>
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-end gap-3 text-muted">
                    <button onClick={() => downloadFinancePdf(dc, biz)} title="Download PDF" className="hover:text-espresso"><Download size={16} /></button>
                    <button onClick={() => emailDoc(dc)} disabled={busy || dc.status === 'void'} title="Email to client" className="hover:text-gold-deep disabled:opacity-30"><Mail size={16} /></button>
                    {dc.kind !== 'quotation' && dc.status !== 'paid' && dc.status !== 'void' && (
                      <button onClick={() => setStatus(dc, 'paid')} title="Mark paid" className="hover:text-emerald-600"><Check size={16} /></button>
                    )}
                    {dc.status !== 'void' && (
                      <button onClick={() => setStatus(dc, 'void')} title="Void" className="hover:text-blush-deep"><Ban size={16} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center font-body text-sm text-muted">No documents yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="my-8 w-full max-w-2xl bg-cream p-7 shadow-soft" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-espresso">New document</h2>
              <button onClick={() => setOpen(false)} className="text-muted hover:text-espresso"><X size={22} /></button>
            </div>

            {/* Prefill from an existing stay or spa booking */}
            <div className="mt-6 border border-line bg-paper p-4">
              <label className="label">Prefill from</label>
              <div className="grid gap-3 sm:grid-cols-2">
                <select className="field" value={source} onChange={(e) => chooseSource(e.target.value as 'manual' | 'stay' | 'booking' | 'carhire')}>
                  <option value="manual">Blank — enter manually</option>
                  <option value="stay">A room stay</option>
                  <option value="booking">A spa booking</option>
                  <option value="carhire">A car hire</option>
                </select>
                {source === 'stay' && (
                  <select className="field" value={pickedId} onChange={(e) => pickRecord(e.target.value)}>
                    <option value="">{stays.length ? 'Choose a stay…' : 'No stays found'}</option>
                    {stays.map((s) => (
                      <option key={s.id} value={s.id}>{s.fullName} · {s.roomName || 'Room'} · {s.checkIn}</option>
                    ))}
                  </select>
                )}
                {source === 'booking' && (
                  <select className="field" value={pickedId} onChange={(e) => pickRecord(e.target.value)}>
                    <option value="">{bookings.length ? 'Choose a booking…' : 'No bookings found'}</option>
                    {bookings.map((b) => (
                      <option key={b.id} value={b.id}>{b.fullName} · {b.date || 'no date'}{b.time ? ` ${b.time}` : ''}</option>
                    ))}
                  </select>
                )}
                {source === 'carhire' && (
                  <select className="field" value={pickedId} onChange={(e) => pickRecord(e.target.value)}>
                    <option value="">{hires.length ? 'Choose a hire…' : 'No car hires found'}</option>
                    {hires.map((h) => (
                      <option key={h.id} value={h.id}>{h.fullName} · {h.vehicleName || 'Vehicle'} · {h.pickup}</option>
                    ))}
                  </select>
                )}
              </div>
              {source !== 'manual' && (
                <p className="mt-2 font-body text-xs text-muted">Client and line items are filled in for you — edit anything below before creating.</p>
              )}
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label">Type</label>
                <select className="field" value={draft.kind} onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value as Draft['kind'] }))}>
                  <option value="invoice">Invoice</option>
                  <option value="receipt">Receipt</option>
                  <option value="quotation">Quotation</option>
                </select>
              </div>
              <div>
                <label className="label">VAT rate</label>
                <select className="field" value={draft.vatRate} onChange={(e) => setDraft((d) => ({ ...d, vatRate: Number(e.target.value) }))}>
                  <option value={0}>None (0%)</option>
                  <option value={0.14}>14%</option>
                </select>
              </div>
              <div>
                <label className="label">Client name</label>
                <input className="field" list="client-list" value={draft.clientName}
                  onChange={(e) => {
                    const name = e.target.value;
                    const match = clients.find((c) => c.name === name);
                    setDraft((d) => ({ ...d, clientName: name, clientEmail: match?.email ?? d.clientEmail }));
                  }} placeholder="Jane Moeng" />
                <datalist id="client-list">
                  {clients.map((c) => <option key={c.name} value={c.name} />)}
                </datalist>
              </div>
              <div>
                <label className="label">Client email</label>
                <input className="field" type="email" value={draft.clientEmail} onChange={(e) => setDraft((d) => ({ ...d, clientEmail: e.target.value }))} placeholder="jane@email.com" />
              </div>
            </div>

            <p className="eyebrow mt-6 mb-2">Line items</p>
            <div className="space-y-2">
              {draft.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input className="field !py-2 flex-1" list="gh-treatment-options" value={it.description} placeholder="Treatment / room / description"
                    onChange={(e) => {
                      const v = e.target.value;
                      const key = v.trim().toLowerCase();
                      const t = treatments.find((x) => x.name.toLowerCase() === key);
                      const r = ROOMS.find((x) => x.name.toLowerCase() === key);
                      const price = t ? t.price : r ? r.price : undefined;
                      setItem(i, price != null ? { description: v, unitPrice: price } : { description: v });
                    }} />
                  <input className="field !py-2 w-16" type="number" min={1} value={it.qty || ''} onChange={(e) => setItem(i, { qty: e.target.value === '' ? 0 : Number(e.target.value) })} title="Qty" />
                  <input className="field !py-2 w-28" type="number" min={0} value={it.unitPrice || ''} onChange={(e) => setItem(i, { unitPrice: e.target.value === '' ? 0 : Number(e.target.value) })} title="Unit price" placeholder="0" />
                  <button onClick={() => setDraft((d) => ({ ...d, items: d.items.filter((_, j) => j !== i) }))} className="text-muted hover:text-blush-deep" aria-label="Remove line"><Trash2 size={15} /></button>
                </div>
              ))}
              <button onClick={() => setDraft((d) => ({ ...d, items: [...d.items, { description: '', qty: 1, unitPrice: 0 }] }))}
                className="inline-flex items-center gap-1.5 font-body text-sm text-gold-deep hover:underline"><Plus size={14} /> Add line</button>
              <datalist id="gh-treatment-options">
                {treatments.map((t) => <option key={t.id} value={t.name}>{formatPula(t.price)}</option>)}
                {ROOMS.map((r) => <option key={r.id} value={r.name}>{formatPula(r.price)} / night</option>)}
              </datalist>
            </div>

            <div className="mt-5">
              <label className="label">Notes (optional)</label>
              <textarea rows={2} className="field" value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} placeholder="Payment terms, thank-you note…" />
            </div>

            {draft.kind === 'receipt' && (
              <div className="mt-4">
                <label className="label">Redeem a gift card (optional)</label>
                <input className="field" value={giftCode} onChange={(e) => setGiftCode(e.target.value)} placeholder="GH-GIFT-XXXXXX" />
                <p className="mt-1 font-body text-xs text-muted">Applies the card's balance to this receipt and updates the card automatically.</p>
              </div>
            )}

            <div className="mt-5 flex items-end justify-between border-t border-line pt-4">
              <div className="font-body text-sm text-muted">
                <p>Subtotal: <span className="text-espresso">{formatPula(subtotal)}</span></p>
                {draft.vatRate > 0 && <p>VAT: <span className="text-espresso">{formatPula(vat)}</span></p>}
                <p className="mt-1 font-display text-xl text-espresso">Total: {formatPula(total)}</p>
              </div>
              <button onClick={create} disabled={busy} className="btn-primary">{busy ? 'Creating…' : 'Create document'}</button>
            </div>
          </div>
        </div>
      )}

      {financeNotice && (
        <Modal onClose={() => setFinanceNotice(null)}>
          <div className="flex items-center gap-3">
            {financeNotice.tone === 'success' ? (
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check size={20} /></span>
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blush-soft text-blush-deep"><Ban size={20} /></span>
            )}
            <h2 className="font-display text-2xl text-espresso">{financeNotice.title}</h2>
          </div>
          <div className="rule-gold mt-4" />
          <p className="mt-4 font-body text-sm leading-relaxed text-muted">{financeNotice.body}</p>
          <div className="mt-6 flex justify-end">
            <button onClick={() => setFinanceNotice(null)} className="btn-primary">Done</button>
          </div>
        </Modal>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>
      )}
    </div>
  );
}
